import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
	AutocompleteElement,
	PasswordElement,
	SelectElement,
	TextFieldElement,
} from 'react-hook-form-mui';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../context/auth';
import {
	DEPRECATED__CLAIMS_ONLY_INTERNAL,
	DEPRECATED__CLAIMS_ONLY_LABEL,
	DEPRECATED__FULL_ACCESS_INTERNAL,
	DEPRECATED__FULL_ACCESS_LABEL,
	DEPRECATED__READ_ONLY_INTERNAL,
	DEPRECATED__READ_ONLY_LABEL,
} from '../../../../../data/labelsAndMessages';
import RolesAPI from '../../api/roles';
import UsersApi, {
	AccountResponse,
	AddUserPayload,
	USERS_KEY,
} from '../../api/users';
import {
	UNEDITABLE_FIELD_INFO,
	USERS_FORM_ADD_BUTTON_LABEL,
	USERS_FORM_CANCEL_BUTTON_LABEL,
	USERS_FORM_EDIT_BUTTON_LABEL,
	USERS_FORM_EMAIL_LABEL,
	USERS_FORM_NAME_LABEL,
	USERS_FORM_PASSWORD_LABEL,
	USERS_FORM_PERMISSIONS_LABEL,
	USERS_FORM_ROLES_LABEL,
	USERS_FORM_USERNAME_LABEL,
	USER_MODAL_DISABLE_USER_BUTTON_TEXT,
	USER_MODAL_ENABLE_USER_BUTTON_TEXT,
} from '../../data/constants';
import { UserFormData, UserFormProps } from './Users.types';

const schema = (
	users: AccountResponse[],
	isEdit: boolean,
	rolesAndPermissionEnabled: boolean,
) =>
	yup.object().shape({
		username: yup
			.string()
			.required('Required')
			.min(6, 'Username must be at least 6 characters')
			.test('username_taken', 'Username is taken', (username) => {
				return !users.map((u) => u.username).includes(username);
			}),
		password: isEdit ? yup.string() : yup.string().required('Required'),
		email: yup.string().email('Invalid email'),
		accountType: rolesAndPermissionEnabled
			? yup.string()
			: yup.string().required('Required'),
	});

const UserForm = (props: UserFormProps) => {
	const authData = useAuthContext();
	const { tenantHostname } = useParams();
	const authorizationToken =
		authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const {
		handleClose,
		users,
		defaultValues,
		isEdit,
		rolesAndPermissionEnabled,
	} = props;

	const { mutate: addUser } = UsersApi.useAddUserMutation(
		[USERS_KEY, tenantId || tenantHostname],
		tenantHostname,
		authorizationToken,
		handleClose,
	);

	const { mutate: updateUser } = UsersApi.useUpdateUserMutation(
		[USERS_KEY, tenantId || tenantHostname],
		tenantHostname,
		authorizationToken,
		defaultValues?.userLocator,
		handleClose,
	);

	const { mutate: toggleUserStatus } = UsersApi.useToggleUserStatusMutation(
		[USERS_KEY, tenantId || tenantHostname],
		tenantHostname,
		authorizationToken,
		defaultValues,
		handleClose,
	);

	const { data: roles } = RolesAPI.useRoles(
		[RolesAPI.ROLES_KEY, tenantId || tenantHostname],
		tenantHostname,
		authorizationToken,
	);

	const permissionOptions = [
		{
			id: DEPRECATED__CLAIMS_ONLY_INTERNAL(),
			label: DEPRECATED__CLAIMS_ONLY_LABEL(),
		},
		{
			id: DEPRECATED__FULL_ACCESS_INTERNAL(),
			label: DEPRECATED__FULL_ACCESS_LABEL(),
		},
		{
			id: DEPRECATED__READ_ONLY_INTERNAL(),
			label: DEPRECATED__READ_ONLY_LABEL(),
		},
	];

	const { handleSubmit, control } = useForm<UserFormData>({
		mode: 'onChange',
		resolver: yupResolver(
			schema(users || [], isEdit, rolesAndPermissionEnabled),
		),
		defaultValues: {
			username: defaultValues?.username || '',
			name: defaultValues?.name || '',
			email: defaultValues?.email || '',
			accountType: defaultValues?.accountType || '',
			password: '',
			roles:
				defaultValues?.roles?.map((role) => ({
					id: role.name,
					label: role.displayName,
				})) || [],
		},
	});

	const onSubmit: SubmitHandler<UserFormData> = (data) => {
		const request: AddUserPayload = {
			username: data.username,
			name: data.name,
			type: rolesAndPermissionEnabled ? 'account.useRoles' : data.accountType,
			password: data.password,
			roles: data.roles.map((role) => role.id),
		};
		if (data.password === '') {
			delete request.password;
		}
		if (data.email && data.email !== '') {
			request.email = data.email;
		}
		isEdit ? updateUser(request) : addUser(request);
	};

	const onToggleStatus = () => {
		toggleUserStatus(defaultValues);
	};

	return (
		<Box
			onSubmit={handleSubmit(onSubmit)}
			component='form'
			sx={{ pt: 1, pb: 2 }}
		>
			<Stack direction='column' spacing={2} sx={{ pb: 2 }}>
				<TextFieldElement
					autoFocus={!isEdit}
					name='username'
					label={USERS_FORM_USERNAME_LABEL()}
					variant='outlined'
					control={control}
					disabled={isEdit}
					helperText={UNEDITABLE_FIELD_INFO()}
				/>
				{!rolesAndPermissionEnabled && (
					<SelectElement
						name='accountType'
						label={USERS_FORM_PERMISSIONS_LABEL()}
						variant='outlined'
						disabled={isEdit}
						control={control}
						options={permissionOptions}
						helperText={UNEDITABLE_FIELD_INFO()}
					/>
				)}
				{rolesAndPermissionEnabled && (
					<AutocompleteElement
						multiple
						label={USERS_FORM_ROLES_LABEL()}
						name='roles'
						options={
							roles?.roles?.map((role) => ({
								id: role.name,
								label: role.displayName,
							})) || []
						}
						control={control}
						autocompleteProps={{
							fullWidth: true,
							limitTags: 2,
						}}
						textFieldProps={{
							variant: 'outlined',
							disabled: !roles?.roles,
						}}
					/>
				)}
				{!tenantHostname && (
					<TextFieldElement
						autoFocus={isEdit}
						name='name'
						label={USERS_FORM_NAME_LABEL()}
						variant='outlined'
						control={control}
					/>
				)}
				{!tenantHostname && (
					<TextFieldElement
						name='email'
						label={USERS_FORM_EMAIL_LABEL()}
						variant='outlined'
						control={control}
					/>
				)}
				{!tenantHostname && (
					<PasswordElement
						name='password'
						label={USERS_FORM_PASSWORD_LABEL()}
						variant='outlined'
						control={control}
					/>
				)}
			</Stack>
			<Stack
				direction='row'
				spacing={2}
				justifyContent={
					isEdit && !tenantHostname ? 'space-between' : 'flex-end'
				}
				sx={{ mt: 2 }}
			>
				{isEdit && !tenantHostname && (
					<Button onClick={onToggleStatus} color='error'>
						{defaultValues.deleted
							? USER_MODAL_ENABLE_USER_BUTTON_TEXT()
							: USER_MODAL_DISABLE_USER_BUTTON_TEXT()}
					</Button>
				)}
				<Stack direction='row' spacing={2} sx={{ mt: 2 }}>
					<Button onClick={handleClose} color='inherit'>
						{USERS_FORM_CANCEL_BUTTON_LABEL()}
					</Button>
					<Button type='submit' variant='contained' color='secondary'>
						{isEdit
							? USERS_FORM_EDIT_BUTTON_LABEL()
							: USERS_FORM_ADD_BUTTON_LABEL()}
					</Button>
				</Stack>
			</Stack>
		</Box>
	);
};

export default UserForm;
